import { ROSTER, type Person } from "@/data/roster";

const byEmail = new Map(ROSTER.map((p) => [p.email.toLowerCase(), p]));
const byAlias = new Map<string, Person>();
for (const p of ROSTER) {
  for (const a of p.aliases) byAlias.set(a.toLowerCase(), p);
  byAlias.set(p.firstName.toLowerCase(), p);
  byAlias.set(p.name.toLowerCase(), p);
}

/** Exact-match resolve of a single owner token or email. */
export function resolvePerson(input: string | undefined | null): Person | undefined {
  if (!input) return undefined;
  const t = input.trim().toLowerCase();
  if (!t) return undefined;
  return byEmail.get(t) ?? byAlias.get(t);
}

/** Owner strings can be "Ryan", "Ryan / Tom", "Stephen, Megan", or even
 * "Ryan / Stephen (hype)". Return every person referenced — primary first. */
export function resolveOwners(raw: string | undefined | null): Person[] {
  if (!raw) return [];
  const tokens = raw
    .split(/[\/,&]|\band\b|\s+\+\s+/i)
    .map((t) => t.trim())
    // strip parenthetical qualifiers: "Ryan (hype)" → "Ryan"
    .map((t) => t.replace(/\(.*?\)/g, "").trim())
    .filter(Boolean);
  const seen = new Set<string>();
  const out: Person[] = [];
  for (const t of tokens) {
    const p = resolvePerson(t);
    if (p && !seen.has(p.email)) {
      seen.add(p.email);
      out.push(p);
    }
  }
  return out;
}

/** Does the given user (by email) own this task/risk/etc.? */
export function isOwnedByEmail(ownerRaw: string | undefined, email: string | undefined | null): boolean {
  if (!email) return false;
  const me = resolvePerson(email);
  if (!me) return false;
  return resolveOwners(ownerRaw).some((p) => p.email === me.email);
}

/** Pretty display name for an owner string; falls back to the raw value. */
export function displayOwner(raw: string | undefined): string {
  if (!raw) return "—";
  const people = resolveOwners(raw);
  if (people.length === 0) return raw;
  return people.map((p) => p.firstName).join(" / ");
}
